<template>
  <div class="mx-auto max-w-4xl space-y-6 p-6">
    <h1 class="text-3xl font-bold text-theme-fg">
      WebRTC Chat
    </h1>

    <Card>
      <CardHeader>
        <CardTitle>Peer-to-peer chat</CardTitle>
        <CardDescription>
          <StatusIndicator
            :status="indicatorStatus"
            :pulse="connectionStatus === 'connecting'"
            :label="connectionStatusText"
          />
        </CardDescription>
      </CardHeader>

      <CardContent class="space-y-6">
        <!-- Role selection -->
        <div v-if="role === null" class="flex flex-col gap-4 sm:flex-row">
          <Button class="flex-1" :loading="isBusy" :disabled="isBusy" @click="startAsHost">
            {{ isBusy ? 'Preparing offer…' : 'Start a chat' }}
          </Button>
          <Button class="flex-1" variant="outline" :disabled="isBusy" @click="joinChat">
            Join a chat
          </Button>
        </div>

        <!-- Host: step 1 show offer, step 2 scan the answer -->
        <div v-if="role === 'host' && connectionStatus !== 'connected'" class="grid gap-6 md:grid-cols-2">
          <section class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              1. Scan this QR with the other device's camera app
            </h3>
            <!-- A camera app opens this link, and camera apps cannot join
                 parts: sized to the screen, never split. -->
            <QrFrames
              v-if="offerLinkValue"
              :value="offerLinkValue"
              level="L"
              :allow-fragments="false"
              alt="Chat offer QR code"
              @error="onQrError"
            />
            <p class="text-center text-xs text-theme-neutral-6">
              It opens this page there and answers on its own.
            </p>
            <div class="flex justify-center">
              <ClipboardButton :text="offerLinkValue" label="Copy link" success-message="Link copied" />
            </div>
          </section>

          <section class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              2. Scan the answer QR shown on the other device
            </h3>
            <QrScanner
              v-if="showScanner && !answerApplied"
              :continuous="false"
              :validate="isAnswer"
              :width="288"
              :height="288"
              video-label="Camera for scanning the answer QR"
              @detected="onAnswerScanned"
              @rejected="onWrongQr('answer')"
              :show-error="false"
              @error="onCameraError"
            />
            <Button v-else-if="!answerApplied" variant="outline" size="sm" @click="showScanner = true">
              Open camera again
            </Button>
            <p v-if="answerApplied" class="text-sm text-theme-ok">
              Answer received — connecting…
            </p>
          </section>

          <details class="md:col-span-2">
            <summary class="cursor-pointer text-sm text-theme-neutral-6">
              No camera? Paste the answer instead
            </summary>
            <label for="answer-input" class="mb-2 mt-3 block text-sm font-medium text-theme-fg">
              Paste the answer produced by the other device
            </label>
            <Textarea
              id="answer-input"
              v-model="answerInput"
              :rows="3"
              class="font-mono text-xs"
              placeholder="a1~…"
            />
          </details>
        </div>

        <!-- Guest: step 1 scan the offer, step 2 show the answer -->
        <div v-if="role === 'guest' && connectionStatus !== 'connected'" class="space-y-4">
          <section v-if="!answerData" class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              1. Scan the offer QR on the device that started the chat
            </h3>
            <QrScanner
              v-if="showScanner"
              :continuous="false"
              :validate="isOffer"
              :width="288"
              :height="288"
              video-label="Camera for scanning the offer QR"
              @detected="onOfferScanned"
              @rejected="onWrongQr('offer')"
              :show-error="false"
              @error="onCameraError"
            />
            <Button v-else variant="outline" size="sm" @click="showScanner = true">
              Open camera again
            </Button>
            <p v-if="isBusy" class="text-sm text-theme-neutral-6">
              Creating answer…
            </p>
            <details>
              <summary class="cursor-pointer text-sm text-theme-neutral-6">
                No camera? Paste the link instead
              </summary>
              <label for="offer-input" class="mb-2 mt-3 block text-sm font-medium text-theme-fg">
                Paste the link from the device that started the chat
              </label>
              <Textarea
                id="offer-input"
                v-model="offerInput"
                :rows="3"
                class="font-mono text-xs"
                placeholder="https://…/chat#o=…"
              />
            </details>
          </section>

          <section v-else class="space-y-3">
            <h3 class="text-sm font-medium text-theme-fg">
              2. Show this answer QR to the device that started the chat — it connects as soon as it is scanned
            </h3>
            <!-- Read by this app's scanner, which joins parts: split when the
                 screen is too small for one readable code. -->
            <QrFrames
              :value="answerData"
              level="L"
              alt="Chat answer QR code"
              @error="onQrError"
            />
            <div class="flex justify-center">
              <ClipboardButton :text="answerData" label="Copy answer" success-message="Answer copied" />
            </div>
          </section>
        </div>

        <!-- Chat: shown only after the data channel really opened -->
        <div v-if="connectionStatus === 'connected'" class="space-y-4">
          <div
            class="h-96 min-h-96 overflow-y-auto rounded-lg bg-theme-surface-2 p-4"
            role="log"
            aria-live="polite"
            aria-label="Chat messages"
          >
            <p v-if="messages.length === 0" class="py-8 text-center text-theme-neutral-6">
              No messages yet. Start the conversation!
            </p>
            <ul v-else class="space-y-2">
              <li
                v-for="(message, index) in messages"
                :key="index"
                class="flex"
                :class="message.type === 'sent' ? 'justify-end' : 'justify-start'"
              >
                <div
                  class="max-w-xs rounded-lg px-4 py-2"
                  :class="message.type === 'sent'
                    ? 'bg-theme-brand text-theme-brand-on'
                    : 'border border-theme-border bg-theme-surface-1 text-theme-fg'"
                >
                  <p class="text-sm">
                    {{ message.text }}
                  </p>
                  <p class="mt-1 text-xs opacity-70">
                    {{ formatTime(message.timestamp) }}
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <form class="flex gap-2" @submit.prevent="sendMessage">
            <Input
              v-model="messageInput"
              class="flex-1"
              placeholder="Type a message…"
              aria-label="Message"
            />
            <Button type="submit" :disabled="!messageInput.trim()">
              Send
            </Button>
          </form>
        </div>

        <Alert v-if="error" variant="destructive">
          <AlertDescription>{{ error }}</AlertDescription>
        </Alert>
      </CardContent>

      <CardFooter v-if="role !== null">
        <Button variant="destructive" @click="disconnect">
          {{ connectionStatus === 'connected' ? 'Disconnect' : 'Cancel' }}
        </Button>
      </CardFooter>
    </Card>

    <!-- Instructions -->
    <Card>
      <CardHeader>
        <CardTitle>How to use WebRTC Chat</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <ol class="list-inside list-decimal space-y-2 text-theme-fg">
          <li>Device A: "Start a chat". It shows an offer QR and turns its camera on.</li>
          <li>Device B: "Join a chat". Its camera turns on; point it at A's QR. B then shows an answer QR.</li>
          <li>Point A's camera at B's answer QR. The chat opens by itself once the data channel is open.</li>
          <li>No camera? Each step also accepts pasted text and continues as soon as it is valid.</li>
        </ol>
        <Alert variant="info">
          <AlertTitle>Privacy</AlertTitle>
          <AlertDescription>
            Messages travel over a WebRTC data channel, which is encrypted with DTLS between the two
            browsers. No messages are stored on servers. The offer and answer contain your network
            candidates (IP addresses); share them only with the person you are chatting with. A TURN
            relay is not configured yet, so networks that block direct connections (for example some
            mobile carriers) will not connect.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { QrScannerError } from '@elveris/ui/qr-scanner'
import { Alert, AlertDescription, AlertTitle } from '@elveris/ui/alert'
import { Button } from '@elveris/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@elveris/ui/card'
import { ClipboardButton } from '@elveris/ui/clipboard-button'
import { Input } from '@elveris/ui/input'
import { QrFrames } from '@elveris/ui/qr-code'
import { QrScanner } from '@elveris/ui/qr-scanner'
import { StatusIndicator } from '@elveris/ui/status-indicator'
import { Textarea } from '@elveris/ui/textarea'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { webrtcManager } from '../utils/webrtc-native'
import { answerJsonFrom, offerJsonFrom, offerLink, offerTokenFromHash, tokenFromManagerJson } from '../utils/sdp-compact'

// Rebuilt on @elveris/ui components (2026-09-30). The connection logic is the
// one verified end to end on 2026-09-30 (camera-driven, auto-advancing); only
// the presentation moved to the kit. Changes that came with the kit:
// - the QR scanner is the kit's (1280x720 capture; `validate` rejects a QR of
//   the wrong kind and keeps the camera running, which this view used to do
//   by hand);
// - QR rendering is the kit's <QrCode> (was `qrcode` -> data URL <img>);
// - copying is <ClipboardButton> (shows a visible error when the clipboard is
//   unavailable instead of this view's own handler);
// - colours are theme tokens (follow the active theme / dark mode) instead of
//   fixed Tailwind palette classes.
// Offer as a link (2026-09-30): the host's QR is
// `<origin>/chat#o=<compact offer>` (utils/sdp-compact.ts, ~180 chars instead
// of ~950 chars of JSON, QR version 9 instead of 21). The guest's native
// camera app opens it; this page reads the fragment, accepts the offer and
// shows the answer. The fragment is never sent to a server and is removed from
// the address bar and history as soon as it is read. The answer QR is scanned
// in this page, so it stays a bare token. Pasted legacy JSON still works.
// Archived versions: _archive/simulated-views/, _archive/kit-migration-2026-09-30/

interface Message {
  type: 'sent' | 'received'
  text: string
  timestamp: Date
}

type Status = 'disconnected' | 'connecting' | 'connected' | 'error'

const role = ref<'host' | 'guest' | null>(null)
const connectionId = ref<string | null>(null)
const connectionStatus = ref<Status>('disconnected')
const isBusy = ref(false)
const error = ref('')
const errorMessage = (e: unknown) => (e instanceof Error ? e.message : '') || String(e)

const offerLinkValue = ref('')
const answerInput = ref('')
const answerApplied = ref(false)

const offerInput = ref('')
const answerData = ref('')

const messageInput = ref('')
const messages = ref<Message[]>([])
const showScanner = ref(false)

const CHAT_PATH = '/chat'
const isAnswer = (payload: string) => answerJsonFrom(payload) !== null
const isOffer = (payload: string) => offerJsonFrom(payload, CHAT_PATH) !== null

async function startAsHost() {
  isBusy.value = true
  error.value = ''
  try {
    const { id, qrData } = await webrtcManager.createHostConnection('elveris-chat', 'chat invitation')
    role.value = 'host'
    connectionId.value = id
    connectionStatus.value = 'connecting'
    offerLinkValue.value = offerLink(tokenFromManagerJson(qrData, 'offer'), CHAT_PATH)
    // Step 2 needs the camera: open it right away instead of waiting for a click.
    showScanner.value = true
  }
  catch (e) {
    connectionStatus.value = 'error'
    error.value = `Could not create an offer: ${errorMessage(e)}`
  }
  finally {
    isBusy.value = false
  }
}

function joinChat() {
  role.value = 'guest'
  error.value = ''
  showScanner.value = true
}

function onAnswerScanned(payload: string) {
  showScanner.value = false
  answerInput.value = payload
}

function onOfferScanned(payload: string) {
  showScanner.value = false
  offerInput.value = payload
}

function onWrongQr(expected: 'answer' | 'offer') {
  error.value = expected === 'answer'
    ? 'That QR is not an answer. Scan the answer shown on the other device.'
    : 'That QR is not a chat offer. Scan the QR on the device that started the chat.'
}

function onCameraError(e: QrScannerError) {
  // Append, don't replace: when a broken offer link opened the camera, its
  // explanation must survive a denied camera permission.
  const camera = `${e.message} You can paste the text instead.`
  error.value = error.value ? `${error.value} ${camera}` : camera
}

function onQrError(e: Error) {
  // A full SDP can exceed single-QR capacity; say so instead of hiding it.
  error.value = `QR could not be generated (${e.message}). Use the copy button instead.`
}

// Continue automatically as soon as a complete, correct handshake is present,
// whether it came from the camera or was pasted. No extra button press.
watch(answerInput, (text) => {
  if (isAnswer(text) && !answerApplied.value && !isBusy.value) applyAnswer()
})
watch(offerInput, (text) => {
  if (isOffer(text) && !answerData.value && !isBusy.value) acceptOffer()
})

async function applyAnswer() {
  if (!connectionId.value) return
  isBusy.value = true
  error.value = ''
  try {
    await webrtcManager.completeConnection(connectionId.value, answerJsonFrom(answerInput.value)!)
    // Still 'connecting': only the channel's 'open' event means connected.
    answerApplied.value = true
  }
  catch (e) {
    error.value = `The answer could not be applied: ${errorMessage(e)}`
  }
  finally {
    isBusy.value = false
  }
}

async function acceptOffer() {
  isBusy.value = true
  error.value = ''
  try {
    const { id, answerData: answer } = await webrtcManager.acceptConnection(offerJsonFrom(offerInput.value, CHAT_PATH)!)
    connectionId.value = id
    connectionStatus.value = 'connecting'
    answerData.value = tokenFromManagerJson(answer, 'answer')
  }
  catch (e) {
    error.value = `The offer could not be accepted: ${errorMessage(e)}`
  }
  finally {
    isBusy.value = false
  }
}

function sendMessage() {
  const text = messageInput.value.trim()
  if (!text || !connectionId.value) return
  if (!webrtcManager.sendMessage(connectionId.value, text)) {
    error.value = 'Message was not sent: the data channel is not open.'
    return
  }
  addMessage('sent', text)
  messageInput.value = ''
}

function addMessage(type: 'sent' | 'received', text: string) {
  messages.value.push({ type, text, timestamp: new Date() })
}

function onMessage(event: Event) {
  const { connectionId: id, message } = (event as CustomEvent).detail
  if (id === connectionId.value) addMessage('received', String(message))
}

function onState(event: Event) {
  const { connectionId: id, state } = (event as CustomEvent).detail
  if (id !== connectionId.value) return
  if (state === 'open') {
    connectionStatus.value = 'connected'
    showScanner.value = false
    error.value = ''
  }
  else if (state === 'closed') {
    connectionStatus.value = 'disconnected'
  }
  else if (state === 'error') {
    connectionStatus.value = 'error'
    error.value = 'The data channel reported an error.'
  }
}

function disconnect() {
  if (connectionId.value) webrtcManager.closeConnection(connectionId.value)
  connectionId.value = null
  role.value = null
  showScanner.value = false
  connectionStatus.value = 'disconnected'
  offerLinkValue.value = ''
  answerInput.value = ''
  answerApplied.value = false
  offerInput.value = ''
  answerData.value = ''
  messages.value = []
  messageInput.value = ''
  error.value = ''
}

const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

const indicatorStatus = computed(() => {
  switch (connectionStatus.value) {
    case 'connected': return 'online'
    case 'connecting': return 'warning'
    case 'error': return 'error'
    default: return 'offline'
  }
})

const connectionStatusText = computed(() => {
  switch (connectionStatus.value) {
    case 'connected':
      return 'Connected — data channel open'
    case 'connecting':
      return role.value === 'host' && !answerApplied.value
        ? 'Waiting for the answer from the other device'
        : 'Connecting…'
    case 'error':
      return 'Connection error'
    default:
      return 'Disconnected'
  }
})

// Opened from the host's QR by the camera app: `#o=<offer>`.
function consumeOfferHash() {
  const hash = window.location.hash
  if (!hash.startsWith('#o=')) return
  // Drop the offer from the address bar and history either way; keep
  // vue-router's history.state intact.
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
  const token = offerTokenFromHash(hash)
  if (connectionId.value !== null) {
    error.value = 'A session is already open on this page. Disconnect it first, then open the link again.'
    return
  }
  role.value = 'guest'
  // Validate the whole token: a truncated or edited link passes the prefix
  // check in offerTokenFromHash but does not expand, and the watcher would
  // then ignore it silently.
  if (token && isOffer(token)) {
    offerInput.value = token // the watcher accepts it
  }
  else {
    showScanner.value = true
    error.value = 'The link did not contain a complete offer. Scan the QR on the other device again.'
  }
}

onMounted(() => {
  window.addEventListener('webrtc-message', onMessage)
  window.addEventListener('webrtc-state', onState)

  consumeOfferHash()
  // The camera app may hand the link to a tab that already shows this page;
  // then only the fragment changes and onMounted does not run again.
  window.addEventListener('hashchange', consumeOfferHash)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', consumeOfferHash)
  window.removeEventListener('webrtc-message', onMessage)
  window.removeEventListener('webrtc-state', onState)
  if (connectionId.value) webrtcManager.closeConnection(connectionId.value)
})
</script>
